import React, {
  ComponentType, useCallback, useContext, useEffect, useState
} from 'react';

import { debounce, useEventListener } from '@codex-brightworld/helpers';
import themes, { resolveByTheme } from '@codex-brightworld/themes';

const currentTheme = resolveByTheme(theme => themes[theme], 'currys');

const MEDIA_TYPE_DEFAULT = 'default';
const MEDIA_TYPE_MOBILE = 'mobile';
const MEDIA_TYPE_TABLET = 'tablet';
const MEDIA_TYPE_TOUCH = 'touch';
const MEDIA_TYPE_DESKTOP = 'desktop';
const MEDIA_TYPE_DESKTOP_SMALL = 'desktopSmall';
const MEDIA_TYPE_DESKTOP_WIDE = 'desktopWide';
const SCROLL_DEBOUNCE_TIMEOUT = 60;

const defaultDeviceInfo = {
  isDetected: false,
  deviceType: MEDIA_TYPE_DESKTOP,
  isDesktopSmall: false,
  isDesktopWide: false,
  isDesktop: true,
  isTablet: false,
  isMobile: false,
  isTouch: false,
  isMSEdge: false,
  isIOS: false,
  isAndroid: false,
  isWindowsPhone: false,
  supportsTouch: false
};

const DeviceContext = React.createContext(defaultDeviceInfo);

export const useDevice = () => useContext(DeviceContext);

const getMobileOS = () => {
  const os = {
    isIOS: false,
    isAndroid: false,
    isWindowsPhone: false
  };

  if (typeof window === 'undefined') {
    return os;
  }

  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  if (/iPad/i.exec(userAgent) || /iPhone/i.exec(userAgent) || /iPod/i.exec(userAgent)) {
    os.isIOS = true;
  }
  if (/Android/i.exec(userAgent)) {
    os.isAndroid = true;
  }

  if (/IEMobile/i.exec(userAgent)) {
    os.isWindowsPhone = true;
  }

  return os;
};

const isMSEdge = () => (typeof window === 'undefined' ? false : window.navigator.userAgent.includes('Edg'));

const supportsTouch = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return 'ontouchstart' in window || 'msmaxtouchpoints' in window.navigator;
};

const getDeviceInfo = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  let deviceType: string;

  if (window.matchMedia(currentTheme.breakpoints.mobile).matches) {
    deviceType = MEDIA_TYPE_MOBILE;
  } else if (window.matchMedia(currentTheme.breakpoints.tablet).matches) {
    deviceType = MEDIA_TYPE_TABLET;
  } else if (window.matchMedia(currentTheme.breakpoints.wide).matches) {
    deviceType = MEDIA_TYPE_DESKTOP_WIDE;
  } else {
    deviceType = MEDIA_TYPE_DESKTOP_SMALL;
  }

  return {
    isDetected: true,
    deviceType,
    isDesktopSmall: deviceType === MEDIA_TYPE_DESKTOP_SMALL,
    isDesktopWide: deviceType === MEDIA_TYPE_DESKTOP_WIDE,
    isDesktop: deviceType === MEDIA_TYPE_DESKTOP_SMALL || deviceType === MEDIA_TYPE_DESKTOP_WIDE,
    isTablet: deviceType === MEDIA_TYPE_TABLET,
    isMobile: deviceType === MEDIA_TYPE_MOBILE,
    isTouch: deviceType === MEDIA_TYPE_TABLET || deviceType === MEDIA_TYPE_MOBILE,
    supportsTouch: supportsTouch(),
    isMSEdge: isMSEdge(),
    ...getMobileOS()
  };
};

type Props = {
  children: JSX.Element | JSX.Element[];
};

export const DeviceProvider = (props: Props) => {
  const [deviceInfo, setDeviceInfo] = useState(defaultDeviceInfo);

  const handleResize = useCallback(
    debounce(() => setDeviceInfo(getDeviceInfo()!), SCROLL_DEBOUNCE_TIMEOUT),
    []
  );

  useEventListener('resize', handleResize, { passive: true }, typeof window === 'undefined' ? undefined : window);

  useEffect(() => {
    setDeviceInfo(getDeviceInfo()!);
  }, []);

  return <DeviceContext.Provider value={deviceInfo}>{props.children}</DeviceContext.Provider>;
};

type BaseComponentProps = Record<string, unknown>;

type BaseComponentType<T> = ComponentType<T> & {
  propTypes: Record<string, any>;
};

/**
 * Map every responsive prop object to single value according to current device type.
 * @param BaseComponent
 * @returns function
 */
export const mapResponsiveProps = <T extends BaseComponentProps>(BaseComponent: BaseComponentType<T>) => (props: T) => {
  const { deviceType, isTouch, isDesktop } = useDevice();
  const mappedProps = {} as any;

  Object.entries(props).forEach(([propName, value]: [string, any]) => {
    switch (true) {
      case typeof value !== 'object' || BaseComponent.propTypes[propName] === undefined: {
        mappedProps[propName] = value;
        break;
      }
      case !!value[deviceType]: {
        mappedProps[propName] = value[deviceType];
        break;
      }
      case isTouch: {
        mappedProps[propName] = value[MEDIA_TYPE_TOUCH] || value[MEDIA_TYPE_DEFAULT];
        break;
      }
      case isDesktop: {
        mappedProps[propName] = value[MEDIA_TYPE_DESKTOP] || value[MEDIA_TYPE_DEFAULT];
        break;
      }
      default: {
        mappedProps[propName] = value[MEDIA_TYPE_DEFAULT];
        break;
      }
    }
  });

  return <BaseComponent {...mappedProps} />;
};

/**
 * Component that dynamically provides information about current device. Works only on client.
 * @component
 */
export const Device = DeviceContext.Consumer;
